import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import Sheet from '@mui/joy/Sheet';
import { Button, Stack, Typography, Box, Skeleton, Alert } from '@mui/joy';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { PlusIcon } from 'lucide-react';
import { useSWRWithAuth as useSWR, useAPI } from 'renderer/lib/authContext';
import { Link as RouterLink } from 'react-router-dom';

import * as chatAPI from 'renderer/lib/transformerlab-api-sdk';
import { useExperimentInfo } from 'renderer/lib/ExperimentInfoContext';
import { fetcher } from 'renderer/lib/transformerlab-api-sdk';
import { useAuth } from 'renderer/lib/authContext';
import { useNotification } from 'renderer/components/Shared/NotificationSystem';
import SafeJSONParse from 'renderer/components/Shared/SafeJSONParse';
import NewInteractiveTaskModal from '../Tasks/NewInteractiveTaskModal';
import EditInteractiveTaskModal from '../Tasks/EditInteractiveTaskModal';
import DeleteTaskConfirmModal from '../Tasks/DeleteTaskConfirmModal';
import InteractiveJobCard from './InteractiveJobCard';
import JobsPanel from '../Tasks/JobsPanel';
import JobsList from '../Tasks/JobsList';
import FileBrowserModal from '../Tasks/FileBrowserModal';
import { API_URL } from 'renderer/lib/api-client/urls';
import { isJobStopPending } from 'renderer/lib/utils';
import { getPreferredProviderId } from '../Tasks/providerCompatibility';

const duration = require('dayjs/plugin/duration');

dayjs.extend(duration);
dayjs.extend(relativeTime);

const NGROK_AUTH_TOKEN_SPECIAL_SECRET_KEY = '_NGROK_AUTH_TOKEN' as const;
const NGROK_AUTH_TOKEN_SECRET_LABEL = 'ngrok auth token';

const REQUIRED_SPECIAL_SECRETS = [
  { key: '_HF_TOKEN', label: 'Hugging Face token' },
  {
    key: NGROK_AUTH_TOKEN_SPECIAL_SECRET_KEY,
    label: NGROK_AUTH_TOKEN_SECRET_LABEL,
  },
] as const;

type SpecialSecretStatus = {
  exists?: boolean;
};

/** Interactive tasks may have no stored provider_id; prefer default provider, then first provider. */
function defaultLaunchProviderId(
  providers: { id?: string; type?: string; is_default?: boolean }[],
): string | null {
  const preferred = getPreferredProviderId(providers);
  return preferred || null;
}

/** Local interactive runs do not use ngrok; never send token placeholder or secret to the provider. */
function omitNgrokAuthTokenForLocal(
  env: Record<string, string> | undefined,
  isLocalProvider: boolean,
): Record<string, string> {
  const out = { ...(env || {}) };
  if (isLocalProvider) {
    delete out.NGROK_AUTH_TOKEN;
  }
  return out;
}

export default function Interactive() {
  const [interactiveModalOpen, setInteractiveModalOpen] = useState(false);
  const [interactiveModalError, setInteractiveModalError] = useState<
    string | null
  >(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [taskBeingEdited, setTaskBeingEdited] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<{
    id: string;
    name?: string;
  } | null>(null);
  const [launchProgressByJobId, setLaunchProgressByJobId] = useState<
    Record<string, { phase?: string; percent?: number; message?: string }>
  >({});
  const [viewFileBrowserFromJob, setViewFileBrowserFromJob] = useState<
    string | null
  >(null);
  const [missingSpecialSecrets, setMissingSpecialSecrets] = useState<string[]>(
    [],
  );
  const [isCheckingSpecialSecrets, setIsCheckingSpecialSecrets] =
    useState(false);
  const [stopPendingByJobId, setStopPendingByJobId] = useState<
    Record<string, boolean>
  >({});

  const { experimentInfo } = useExperimentInfo();
  const { addNotification } = useNotification();
  const { fetchWithAuth, team } = useAuth();

  // Trigger to force re-render when localStorage changes
  const [pendingIdsTrigger, setPendingIdsTrigger] = useState(0);

  const {
    data: providerListData,
    error: providerListError,
    isLoading: providersIsLoading,
  } = useAPI('compute_provider', ['list'], { teamId: team?.id ?? null });

  const providers = useMemo(
    () => (Array.isArray(providerListData) ? providerListData : []),
    [providerListData],
  );
  const hasNonLocalProvider = useMemo(
    () => providers.some((provider) => provider?.type !== 'local'),
    [providers],
  );

  useEffect(() => {
    if (providerListError) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch providers', providerListError);
    }
  }, [providerListError]);

  useEffect(() => {
    let cancelled = false;

    const checkSpecialSecrets = async () => {
      setIsCheckingSpecialSecrets(true);

      try {
        const userSecretsPromise = fetchWithAuth(
          `${API_URL()}users/me/special_secrets`,
        );
        const teamSecretsPromise = team?.id
          ? fetchWithAuth(`${API_URL()}teams/${team.id}/special_secrets`)
          : Promise.resolve(null);

        const [userRes, teamRes] = await Promise.all([
          userSecretsPromise,
          teamSecretsPromise,
        ]);

        const userData = userRes?.ok ? await userRes.json() : null;
        const teamData = teamRes && teamRes.ok ? await teamRes.json() : null;

        const userSpecialSecrets = (userData?.special_secrets || {}) as Record<
          string,
          SpecialSecretStatus
        >;
        const teamSpecialSecrets = (teamData?.special_secrets || {}) as Record<
          string,
          SpecialSecretStatus
        >;

        const requiredSecrets = REQUIRED_SPECIAL_SECRETS.filter(({ key }) => {
          if (key === NGROK_AUTH_TOKEN_SPECIAL_SECRET_KEY) {
            return hasNonLocalProvider;
          }
          return true;
        });

        const missing = requiredSecrets
          .filter(({ key }) => {
            const userHasSecret = Boolean(userSpecialSecrets[key]?.exists);
            const teamHasSecret = Boolean(teamSpecialSecrets[key]?.exists);
            return !userHasSecret && !teamHasSecret;
          })
          .map(({ label }) => label);

        if (!cancelled) {
          setMissingSpecialSecrets(missing);
        }
      } catch (error) {
        console.error('Failed to check interactive special secrets:', error);
        if (!cancelled) {
          setMissingSpecialSecrets([]);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingSpecialSecrets(false);
        }
      }
    };

    checkSpecialSecrets();

    return () => {
      cancelled = true;
    };
  }, [fetchWithAuth, team?.id, hasNonLocalProvider]);

  // Pending job IDs persisted per experiment
  const pendingJobsStorageKey = useMemo(
    () =>
      experimentInfo?.id
        ? `pendingJobIds:${String(experimentInfo.id)}`
        : 'pendingJobIds:unknown',
    [experimentInfo?.id],
  );

  const getPendingJobIds = useCallback((): string[] => {
    const raw = window.localStorage.getItem(pendingJobsStorageKey);
    if (!raw) return [];
    const parsed = SafeJSONParse(raw, []);
    const result = Array.isArray(parsed) ? parsed : [];
    return result;
  }, [pendingJobsStorageKey]);

  const setPendingJobIds = useCallback(
    (ids: string[]) => {
      try {
        window.localStorage.setItem(pendingJobsStorageKey, JSON.stringify(ids));
        setPendingIdsTrigger((prev) => prev + 1);
      } catch {
        // ignore storage failures
      }
    },
    [pendingJobsStorageKey],
  );

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === pendingJobsStorageKey) {
        setPendingIdsTrigger((prev) => prev + 1);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [pendingJobsStorageKey]);

  // Fetch REMOTE jobs with interactive subtype
  const {
    data: jobsRemote,
    isError: jobsIsError,
    isLoading: jobsIsLoading,
    mutate: jobsMutate,
  } = useSWR(
    experimentInfo?.id
      ? chatAPI.Endpoints.Jobs.ListWithFilters(
          experimentInfo.id,
          'REMOTE',
          '',
          'interactive',
        )
      : null,
    fetcher,
    {
      refreshInterval: 3000,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      refreshWhenHidden: true,
      refreshWhenOffline: false,
    },
  );

  const jobs = useMemo(() => {
    return Array.isArray(jobsRemote) ? jobsRemote : [];
  }, [jobsRemote]);

  const handleStopPendingChange = useCallback(
    (jobId: string, stopPending: boolean) => {
      setStopPendingByJobId((prev) => {
        if (stopPending) {
          return { ...prev, [jobId]: true };
        }
        if (!prev[jobId]) return prev;
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (!Array.isArray(jobs) || jobs.length === 0) return;
    setStopPendingByJobId((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const job of jobs as any[]) {
        const id = String(job?.id ?? '');
        if (!id || !next[id]) continue;
        if (isJobStopPending(job?.status, job?.job_data?.stop_requested)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [jobs]);

  // Derive a stable string of job IDs that need launch-progress polling.
  // This avoids resetting the 3s interval on every SWR revalidation.
  const launchPollingJobIds = useMemo(() => {
    if (!jobs || !Array.isArray(jobs)) return '';
    return jobs
      .filter(
        (job: {
          type?: string;
          status?: string;
          job_data?: { provider_id?: string };
        }) =>
          job.type === 'REMOTE' &&
          job.job_data?.provider_id &&
          (job.status === 'LAUNCHING' || job.status === 'WAITING'),
      )
      .map((job: { id: string | number }) => String(job.id))
      .sort()
      .join(',');
  }, [jobs]);

  // Keep a ref to the latest fetchWithAuth/jobsMutate so the interval callback
  // always uses current values without being in the dependency array.
  const fetchWithAuthRef = useRef(fetchWithAuth);
  const jobsMutateRef = useRef(jobsMutate);
  useEffect(() => {
    fetchWithAuthRef.current = fetchWithAuth;
  }, [fetchWithAuth]);
  useEffect(() => {
    jobsMutateRef.current = jobsMutate;
  }, [jobsMutate]);

  // Poll REMOTE jobs in LAUNCHING/WAITING for live launch_progress (same pattern as Tasks).
  useEffect(() => {
    if (!launchPollingJobIds || !experimentInfo?.id) return;

    const ids = launchPollingJobIds.split(',');
    const experimentId = String(experimentInfo.id);

    const checkJobs = async () => {
      for (const jobId of ids) {
        try {
          const response = await fetchWithAuthRef.current(
            chatAPI.Endpoints.ComputeProvider.CheckJobStatus(
              jobId,
              experimentId,
            ),
            { method: 'GET' },
          );
          if (response.ok) {
            const result = await response.json();
            if (
              result.current_status === 'COMPLETE' ||
              result.current_status === 'FAILED' ||
              result.current_status === 'STOPPED'
            ) {
              setLaunchProgressByJobId((prev) => {
                const next = { ...prev };
                delete next[jobId];
                return next;
              });
              setTimeout(() => jobsMutateRef.current(), 0);
            } else if (result.launch_progress) {
              setLaunchProgressByJobId((prev) => ({
                ...prev,
                [jobId]: result.launch_progress,
              }));
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Failed to check job ${jobId}:`, error);
        }
      }
    };

    checkJobs();
    const interval = setInterval(checkJobs, 3000);
    return () => clearInterval(interval);
  }, [launchPollingJobIds, experimentInfo?.id]);

  // Fetch templates with interactive subtype
  const {
    data: allTemplates,
    isError: templatesIsError,
    isLoading: templatesIsLoading,
    mutate: templatesMutate,
  } = useSWR(
    experimentInfo?.id
      ? chatAPI.Endpoints.Task.ListBySubtypeInExperiment(
          experimentInfo.id,
          'interactive',
          'REMOTE',
        )
      : null,
    fetcher,
  );

  const tasks =
    (Array.isArray(allTemplates)
      ? allTemplates
      : allTemplates?.data || []
    )?.filter((template: any) => {
      return template.experiment_id === experimentInfo?.id;
    }) || [];

  // Remove pending placeholders that are now present in jobs
  useEffect(() => {
    if (!jobs || !Array.isArray(jobs)) return;
    const pending = getPendingJobIds();
    if (pending.length === 0) return;
    const existingIds = new Set((jobs as any[]).map((j: any) => String(j.id)));
    const stillPending = pending.filter((id) => !existingIds.has(String(id)));
    if (stillPending.length !== pending.length) {
      setPendingJobIds(stillPending);
    }
  }, [jobs, getPendingJobIds, setPendingJobIds]);

  // Build list with placeholders for pending job IDs
  const jobsWithPlaceholders = useMemo(() => {
    const baseJobs = Array.isArray(jobs) ? jobs : [];

    // Show active interactive jobs
    const filteredJobs = baseJobs.filter((job: any) => {
      return (
        job.status === 'INTERACTIVE' ||
        job.status === 'RUNNING' ||
        job.status === 'LAUNCHING' ||
        job.status === 'WAITING' ||
        job.status === 'STOPPING'
      );
    });

    const pending = getPendingJobIds();

    if (!pending.length) return filteredJobs;

    const allExistingIds = new Set(baseJobs.map((j: any) => String(j.id)));

    const placeholders = pending
      .filter((id) => !allExistingIds.has(String(id)))
      .map((id) => ({
        id: String(id),
        type: 'REMOTE',
        status: 'CREATED',
        progress: 0,
        job_data: { subtype: 'interactive' },
        placeholder: true,
      }));

    return [...placeholders, ...filteredJobs];
  }, [jobs, getPendingJobIds, pendingIdsTrigger]);

  const jobsWithUiState = useMemo(() => {
    return jobsWithPlaceholders.map((job: any) => {
      const id = String(job?.id ?? '');
      if (!id || !stopPendingByJobId[id]) return job;
      return {
        ...job,
        job_data: {
          ...(job?.job_data || {}),
          stop_requested: true,
        },
      };
    });
  }, [jobsWithPlaceholders, stopPendingByJobId]);

  // Completed / failed / stopped interactive jobs for the History section
  const historyJobs = useMemo(() => {
    const baseJobs = Array.isArray(jobs) ? jobs : [];
    return baseJobs.filter((job: any) => {
      return (
        job.status === 'COMPLETE' ||
        job.status === 'FAILED' ||
        job.status === 'STOPPED'
      );
    });
  }, [jobs]);

  const handleDeleteTask = (taskId: string, taskName?: string) => {
    setTaskToDelete({ id: taskId, name: taskName });
  };

  const handleConfirmDeleteTask = useCallback(
    async (taskId: string): Promise<boolean> => {
      if (!experimentInfo?.id) return false;
      try {
        const response = await chatAPI.authenticatedFetch(
          chatAPI.Endpoints.Task.DeleteTemplate(experimentInfo.id, taskId),
          { method: 'GET' },
        );
        if (response.ok) {
          addNotification({
            type: 'success',
            message: 'Template deleted successfully!',
          });
          await templatesMutate();
          return true;
        }
        addNotification({
          type: 'danger',
          message: 'Failed to delete template. Please try again.',
        });
        return false;
      } catch (error) {
        console.error('Error deleting template:', error);
        addNotification({
          type: 'danger',
          message: 'Failed to delete template. Please try again.',
        });
        return false;
      }
    },
    [experimentInfo?.id, addNotification, templatesMutate],
  );

  const handleDeleteJob = async (jobId: string) => {
    if (!experimentInfo?.id) return;

    // eslint-disable-next-line no-alert
    if (!confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      const response = await chatAPI.authenticatedFetch(
        chatAPI.Endpoints.Jobs.Delete(experimentInfo.id, jobId),
        {
          method: 'DELETE',
        },
      );

      if (response.ok) {
        addNotification({
          type: 'success',
          message: 'Job deleted successfully!',
        });
        await jobsMutate();
      } else {
        addNotification({
          type: 'danger',
          message: 'Failed to delete job. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      addNotification({
        type: 'danger',
        message: 'Failed to delete job. Please try again.',
      });
    }
  };

  const handleExportTemplateToTeamInteractiveGallery = useCallback(
    async (taskId: string) => {
      if (!experimentInfo?.id) return;
      try {
        const response = await chatAPI.authenticatedFetch(
          chatAPI.Endpoints.Task.ExportToTeamGallery(experimentInfo.id),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ task_id: taskId }),
          },
        );

        if (!response.ok) {
          const txt = await response.text();
          addNotification({
            type: 'danger',
            message: `Failed to export template: ${txt}`,
          });
          return;
        }

        const result = await response.json();
        addNotification({
          type: 'success',
          message:
            result?.message ||
            'Template exported to Team Interactive Gallery successfully.',
        });
      } catch (error) {
        console.error('Error exporting template:', error);
        addNotification({
          type: 'danger',
          message: 'Failed to export template. Please try again.',
        });
      }
    },
    [experimentInfo?.id, addNotification],
  );

  const handleSubmitInteractive = async (
    data: any,
    shouldLaunch: boolean = false,
  ) => {
    if (!experimentInfo?.id) {
      addNotification({ type: 'warning', message: 'No experiment selected' });
      return;
    }

    if (!providers.length) {
      addNotification({
        type: 'danger',
        message:
          'No providers available. Add a provider in the team settings first.',
      });
      return;
    }

    const providerMeta =
      providers.find((p) => p.id === data.provider_id) || providers[0];

    setIsSubmitting(true);
    setInteractiveModalError(null);
    try {
      // Fetch interactive gallery to get setup and run templates
      let defaultSetup: string;
      let defaultRun: string;
      let templateId: string | undefined;
      let template: any;
      let galleryTemplate: any = null;

      try {
        const galleryResponse = await chatAPI.authenticatedFetch(
          chatAPI.Endpoints.Task.InteractiveGallery(experimentInfo.id),
          {
            method: 'GET',
          },
        );

        if (galleryResponse.ok) {
          const galleryData = await galleryResponse.json();
          if (!data.template_id) {
            throw new Error('Interactive template id is required');
          }
          template = galleryData.data?.find(
            (t: any) => t.id === data.template_id,
          );

          if (!template) {
            throw new Error(
              `Template not found for: ${data.template_id || data.interactive_type || 'unknown'}`,
            );
          }

          defaultSetup = template.setup || '';
          defaultRun = template.run || '';
          templateId = template.id;
          galleryTemplate = template;
        } else {
          throw new Error('Failed to fetch interactive gallery');
        }
      } catch (error) {
        throw error;
      }

      let response: Response;
      let templatePayload: any = {};

      if (template.local_task_dir || template.github_repo_url) {
        // Use the gallery import API which reads task.yaml and copies files,
        // just like the "Upload from Local Directory" or GitHub import flow.
        const envVarsForImport: Record<string, string> = {
          ...(data.env_parameters || {}),
        };
        // Remote interactive tasks with exposed ports get auto-ngrok on the server; ensure the
        // team ngrok secret is referenced (same as non-GitHub template path). ollama_gradio etc.
        // do not list NGROK_AUTH_TOKEN in env_parameters, so it would otherwise be missing.
        const galleryPorts = Array.isArray(template?.ports)
          ? template.ports
          : [];
        const hasNgrokField = template?.env_parameters?.some(
          (p: { env_var?: string }) => p.env_var === 'NGROK_AUTH_TOKEN',
        );
        const shouldInjectNgrokSecret =
          providerMeta.type !== 'local' &&
          !envVarsForImport.NGROK_AUTH_TOKEN?.trim() &&
          (galleryPorts.length > 0 || hasNgrokField);
        if (shouldInjectNgrokSecret) {
          envVarsForImport.NGROK_AUTH_TOKEN = `{{secret.${NGROK_AUTH_TOKEN_SPECIAL_SECRET_KEY}}}`;
        }
        const envVarsForImportClean = omitNgrokAuthTokenForLocal(
          envVarsForImport,
          providerMeta.type === 'local',
        );
        response = await chatAPI.authenticatedFetch(
          chatAPI.Endpoints.Task.ImportFromGallery(experimentInfo.id),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gallery_id: templateId,
              experiment_id: experimentInfo.id,
              name: data.title,
              is_interactive: true,
              env_vars:
                Object.keys(envVarsForImportClean).length > 0
                  ? envVarsForImportClean
                  : undefined,
              cpus: data.cpus || undefined,
              memory: data.memory || undefined,
              accelerators: data.accelerators || undefined,
            }),
          },
        );
      } else {
        // Create template with flat structure
        // Use env_parameters from the gallery-defined structure
        const envVars: Record<string, string> = data.env_parameters || {};

        // Check if the template defines NGROK_AUTH_TOKEN in its env_parameters
        const needsNgrok = template?.env_parameters?.some(
          (p: any) => p.env_var === 'NGROK_AUTH_TOKEN',
        );
        if (
          needsNgrok &&
          providerMeta.type !== 'local' &&
          !envVars.NGROK_AUTH_TOKEN
        ) {
          envVars.NGROK_AUTH_TOKEN = `{{secret.${NGROK_AUTH_TOKEN_SPECIAL_SECRET_KEY}}}`;
        }
        const envVarsClean = omitNgrokAuthTokenForLocal(
          envVars,
          providerMeta.type === 'local',
        );

        templatePayload = {
          name: data.title,
          type: 'REMOTE',
          plugin: 'remote_orchestrator',
          experiment_id: experimentInfo.id,
          cluster_name: data.title,
          run: defaultRun,
          cpus: data.cpus || undefined,
          memory: data.memory || undefined,
          accelerators: data.accelerators || undefined,
          setup: defaultSetup,
          subtype: 'interactive',
          interactive_type: template?.interactive_type || undefined,
          interactive_gallery_id: templateId,
          provider_id: providerMeta.id,
          provider_name: providerMeta.name,
          env_vars:
            Object.keys(envVarsClean).length > 0 ? envVarsClean : undefined,
          github_repo_url: galleryTemplate?.github_repo_url || undefined,
          github_repo_dir: galleryTemplate?.github_repo_dir || undefined,
        };

        response = await chatAPI.authenticatedFetch(
          chatAPI.Endpoints.Task.CreateTemplate(experimentInfo?.id || ''),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(templatePayload),
          },
        );
      }

      if (response.ok) {
        const result = await response.json();
        const taskId = result.id || result.data?.id;
        await templatesMutate();

        const interactiveTypeLabel =
          template?.name || data.interactive_type || 'interactive';

        if (shouldLaunch) {
          if (!taskId) {
            setInteractiveModalError(
              'Template was created, but no task ID was returned. Please refresh and try launching from the task list.',
            );
            return;
          }

          // Construct task object for launching. For local_task_dir imports
          // the task metadata lives in the backend, so find it from the
          // refreshed list instead of the (empty) templatePayload.
          const refreshed = await templatesMutate();
          const taskList = Array.isArray(refreshed)
            ? refreshed
            : (refreshed as any)?.data || [];
          const newTask = taskList.find((t: any) => t.id === taskId) || {
            id: taskId,
            ...templatePayload,
          };

          // Launch the task immediately. If launch fails (e.g. missing secrets),
          // keep the modal open and show the error inline.
          const launch = await launchInteractiveTask(newTask, {
            provider_id: data.provider_id,
          });
          if (!launch.ok) {
            setInteractiveModalError(launch.error);
            return;
          }

          addNotification({
            type: 'success',
            message: `Interactive ${interactiveTypeLabel} session launched!`,
          });
        } else {
          addNotification({
            type: 'success',
            message: `Interactive template created. Use Queue to launch the ${interactiveTypeLabel} tunnel.`,
          });
        }

        setInteractiveModalOpen(false);
      } else {
        const txt = await response.text();
        setInteractiveModalError(
          `Failed to create interactive template: ${txt}`,
        );
      }
    } catch (error) {
      console.error('Error creating interactive template:', error);
      setInteractiveModalError(
        'Failed to create interactive template. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const launchInteractiveTask = useCallback(
    async (
      task: any,
      launchOverrides?: { provider_id?: string },
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      if (!experimentInfo?.id) {
        return { ok: false, error: 'No experiment selected.' };
      }

      const cfg =
        task.config !== undefined ? SafeJSONParse(task.config, task) : task;

      // Prefer the provider the user picked in the modal. Imported interactive tasks
      // (e.g. GitHub task.yaml) get provider_id from _resolve_provider on the server
      // (often the first listed provider — frequently local), which would otherwise
      // ignore the user's selection here.
      const providerId =
        launchOverrides?.provider_id ||
        cfg.provider_id ||
        task.provider_id ||
        defaultLaunchProviderId(providers);
      if (!providerId) {
        return {
          ok: false,
          error:
            'No providers available. Add a provider in the team settings first.',
        };
      }

      const providerMeta = providers.find(
        (provider) => provider.id === providerId,
      );

      if (!providerMeta) {
        return {
          ok: false,
          error:
            'Selected provider is unavailable. Please create or update providers in team settings.',
        };
      }

      if (!cfg.run && !cfg.github_repo_url && !task.github_repo_url) {
        return { ok: false, error: 'Something went wrong. Please try again.' };
      }

      const payload = {
        experiment_id: experimentInfo.id,
        task_id: task.id,
        task_name: task.name,
        cluster_name: cfg.cluster_name || task.cluster_name,
        run: cfg.run || task.run,
        subtype: cfg.subtype || task.subtype,
        interactive_type: cfg.interactive_type || task.interactive_type,
        interactive_gallery_id:
          cfg.interactive_gallery_id ??
          task?.interactive_gallery_id ??
          undefined,
        cpus: cfg.cpus || task.cpus,
        memory: cfg.memory || task.memory,
        disk_space: cfg.disk_space || task.disk_space,
        accelerators: cfg.accelerators || task.accelerators,
        num_nodes: cfg.num_nodes || task.num_nodes,
        setup: cfg.setup || task.setup,
        env_vars: omitNgrokAuthTokenForLocal(
          { ...(cfg.env_vars || task.env_vars || {}) },
          providerMeta.type === 'local',
        ),
        parameters: cfg.parameters || task.parameters || undefined,
        file_mounts: cfg.file_mounts || task.file_mounts,
        provider_name: providerMeta.name,
        github_repo_url: cfg.github_repo_url || task.github_repo_url,
        github_repo_dir: cfg.github_repo_dir || task.github_repo_dir,
        github_repo_branch: cfg.github_repo_branch || task.github_repo_branch,
        run_sweeps: cfg.run_sweeps || task.run_sweeps || undefined,
        sweep_config: cfg.sweep_config || task.sweep_config || undefined,
        sweep_metric:
          cfg.sweep_metric ||
          task.sweep_metric ||
          (cfg.run_sweeps || task.run_sweeps ? 'eval/loss' : undefined),
        lower_is_better:
          cfg.lower_is_better !== undefined
            ? cfg.lower_is_better
            : task.lower_is_better !== undefined
              ? task.lower_is_better
              : undefined,
        minutes_requested:
          cfg.minutes_requested || task.minutes_requested || undefined,
        local: providerMeta.type === 'local',
      };

      try {
        const response = await fetchWithAuth(
          chatAPI.Endpoints.ComputeProvider.LaunchTemplate(providerId),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          },
        );

        let launchResult: any = {};
        try {
          launchResult = await response.json();
        } catch {
          launchResult = {};
        }

        if (
          response.ok &&
          (launchResult?.status === 'success' ||
            (launchResult?.status === 'WAITING' && launchResult?.job_id))
        ) {
          const newId = String(launchResult.job_id);
          const pending = getPendingJobIds();
          if (!pending.includes(newId)) {
            setPendingJobIds([newId, ...pending]);
          }
          setTimeout(() => jobsMutate(), 0);
          await Promise.all([jobsMutate(), templatesMutate()]);
          return { ok: true };
        }

        const message =
          launchResult?.detail ||
          launchResult?.message ||
          'Failed to queue provider-backed task.';
        return { ok: false, error: String(message) };
      } catch (e: any) {
        console.error(e);
        return { ok: false, error: 'Failed to queue provider-backed task.' };
      }
    },
    [
      experimentInfo?.id,
      fetchWithAuth,
      getPendingJobIds,
      jobsMutate,
      providers,
      setPendingJobIds,
      templatesMutate,
    ],
  );

  const handleQueue = async (task: any) => {
    if (!experimentInfo?.id) return;

    const cfg =
      task.config !== undefined ? SafeJSONParse(task.config, task) : task;

    const providerId =
      cfg.provider_id || task.provider_id || defaultLaunchProviderId(providers);
    if (!providerId) {
      addNotification({
        type: 'danger',
        message:
          'No providers available. Add a provider in the team settings first.',
      });
      return;
    }

    const providerMeta = providers.find(
      (provider) => provider.id === providerId,
    );

    if (!providerMeta) {
      addNotification({
        type: 'danger',
        message:
          'Selected provider is unavailable. Please create or update providers in team settings.',
      });
      return;
    }

    if (!cfg.run && !cfg.github_repo_url && !task.github_repo_url) {
      addNotification({
        type: 'warning',
        message: 'Task is missing a run command.',
      });
      return;
    }

    addNotification({
      type: 'success',
      message: 'Launching provider job...',
    });

    try {
      const payload = {
        experiment_id: experimentInfo.id,
        task_id: task.id,
        task_name: task.name,
        cluster_name: cfg.cluster_name || task.cluster_name,
        run: cfg.run || task.run,
        subtype: cfg.subtype || task.subtype,
        interactive_type: cfg.interactive_type || task.interactive_type,
        interactive_gallery_id:
          cfg.interactive_gallery_id ??
          task?.interactive_gallery_id ??
          undefined,
        cpus: cfg.cpus || task.cpus,
        memory: cfg.memory || task.memory,
        disk_space: cfg.disk_space || task.disk_space,
        accelerators: cfg.accelerators || task.accelerators,
        num_nodes: cfg.num_nodes || task.num_nodes,
        setup: cfg.setup || task.setup,
        env_vars: omitNgrokAuthTokenForLocal(
          { ...(cfg.env_vars || task.env_vars || {}) },
          providerMeta.type === 'local',
        ),
        parameters: cfg.parameters || task.parameters || undefined,
        file_mounts: cfg.file_mounts || task.file_mounts,
        provider_name: providerMeta.name,
        github_repo_url: cfg.github_repo_url || task.github_repo_url,
        github_repo_dir: cfg.github_repo_dir || task.github_repo_dir,
        github_repo_branch: cfg.github_repo_branch || task.github_repo_branch,
        run_sweeps: cfg.run_sweeps || task.run_sweeps || undefined,
        sweep_config: cfg.sweep_config || task.sweep_config || undefined,
        sweep_metric:
          cfg.sweep_metric ||
          task.sweep_metric ||
          (cfg.run_sweeps || task.run_sweeps ? 'eval/loss' : undefined),
        lower_is_better:
          cfg.lower_is_better !== undefined
            ? cfg.lower_is_better
            : task.lower_is_better !== undefined
              ? task.lower_is_better
              : undefined,
        minutes_requested:
          cfg.minutes_requested || task.minutes_requested || undefined,
        local: providerMeta.type === 'local',
      };

      const response = await fetchWithAuth(
        chatAPI.Endpoints.ComputeProvider.LaunchTemplate(providerId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      let launchResult: any = {};
      try {
        launchResult = await response.json();
      } catch {
        launchResult = {};
      }

      if (
        response.ok &&
        (launchResult?.status === 'success' ||
          (launchResult?.status === 'WAITING' && launchResult?.job_id))
      ) {
        const newId = String(launchResult.job_id);
        const pending = getPendingJobIds();
        if (!pending.includes(newId)) {
          setPendingJobIds([newId, ...pending]);
        }
        setTimeout(() => jobsMutate(), 0);

        addNotification({
          type: 'success',
          message: 'Provider cluster launch initiated.',
        });
        await Promise.all([jobsMutate(), templatesMutate()]);
      } else {
        const message =
          launchResult?.detail ||
          launchResult?.message ||
          'Failed to queue provider-backed task.';
        addNotification({
          type: 'danger',
          message,
        });
      }
    } catch (e) {
      console.error(e);
      addNotification({
        type: 'danger',
        message: 'Failed to queue provider-backed task.',
      });
    }
  };

  const handleEditTask = (task: any) => {
    setTaskBeingEdited(task);
    setEditModalOpen(true);
  };

  const loading = templatesIsLoading || jobsIsLoading;

  return (
    <Sheet
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <NewInteractiveTaskModal
        open={interactiveModalOpen}
        onClose={() => {
          setInteractiveModalOpen(false);
          setInteractiveModalError(null);
          setIsSubmitting(false);
        }}
        submitError={interactiveModalError}
        onClearSubmitError={() => setInteractiveModalError(null)}
        onSubmit={(data, shouldLaunch) =>
          handleSubmitInteractive(data, shouldLaunch)
        }
        isSubmitting={isSubmitting}
        providers={providers}
        isProvidersLoading={providersIsLoading}
        importedTasks={tasks}
        onDeleteTask={handleDeleteTask}
        onQueueTask={handleQueue}
        onRefreshTasks={templatesMutate}
      />
      {taskBeingEdited && (
        <EditInteractiveTaskModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setTaskBeingEdited(null);
          }}
          task={taskBeingEdited}
          providers={providers}
          isProvidersLoading={providersIsLoading}
          onSaved={async (updatedBody: any) => {
            if (!experimentInfo?.id || !taskBeingEdited?.id) {
              addNotification({
                type: 'warning',
                message: 'Missing experiment or task ID',
              });
              return;
            }

            try {
              const response = await chatAPI.authenticatedFetch(
                chatAPI.Endpoints.Task.UpdateTemplate(
                  experimentInfo.id,
                  taskBeingEdited.id,
                ),
                {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                  },
                  body: JSON.stringify(updatedBody),
                },
              );

              if (response.ok) {
                await templatesMutate();
                addNotification({
                  type: 'success',
                  message: 'Interactive task updated successfully',
                });
              } else {
                const txt = await response.text();
                addNotification({
                  type: 'danger',
                  message: `Failed to update interactive task: ${txt}`,
                });
              }
            } catch (error) {
              console.error('Error updating interactive task:', error);
              addNotification({
                type: 'danger',
                message: 'Failed to update interactive task. Please try again.',
              });
            }
          }}
        />
      )}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Typography level="title-md">Running Services</Typography>
        <Button
          startDecorator={<PlusIcon size={16} />}
          onClick={() => setInteractiveModalOpen(true)}
        >
          New
        </Button>
      </Stack>
      {!isCheckingSpecialSecrets && missingSpecialSecrets.length > 0 && (
        <Alert
          color="warning"
          variant="soft"
          sx={{
            mt: 1,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography level="body-sm">
              Interactive sessions may fail without required secrets. Missing:{' '}
              <b>{missingSpecialSecrets.join(', ')}</b>.
              {missingSpecialSecrets.includes(NGROK_AUTH_TOKEN_SECRET_LABEL) &&
                hasNonLocalProvider &&
                ` ${NGROK_AUTH_TOKEN_SECRET_LABEL} is required for interactive tasks on remote providers.`}{' '}
              Set them in{' '}
              <Typography
                component={RouterLink}
                to="/user/secrets"
                level="body-sm"
                sx={{ textDecoration: 'underline' }}
              >
                User Settings
              </Typography>
              .
            </Typography>
          </Box>
        </Alert>
      )}
      <Sheet
        variant="soft"
        sx={{
          px: 2,
          py: 2,
          mt: 1,
          mb: 2,
          flex: 1,
          height: '100%',
          overflow: 'auto',
        }}
      >
        {(jobsIsLoading || !experimentInfo?.id) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            <Skeleton variant="rectangular" height={100} width={100} />
          </Box>
        )}
        {!jobsIsLoading &&
          experimentInfo?.id &&
          jobsWithPlaceholders.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                textAlign: 'center',
              }}
            >
              <Typography level="body-lg" sx={{ mb: 2 }}>
                No running services
              </Typography>
              <Typography level="body-sm" color="neutral">
                Click <b>New</b> to launch an interactive service such as
                Jupyter, VS Code, or an inference server.
              </Typography>
            </Box>
          )}
        {!jobsIsLoading && jobsWithUiState.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2,
            }}
          >
            {jobsWithUiState.map((job: any) => (
              <InteractiveJobCard
                key={job.id}
                job={job}
                onDeleteJob={handleDeleteJob}
                launchProgress={launchProgressByJobId[String(job.id)]}
                onStopPendingChange={handleStopPendingChange}
              />
            ))}
          </Box>
        )}
      </Sheet>
      <JobsPanel
        title="History"
        jobs={historyJobs}
        loading={jobsIsLoading || !experimentInfo?.id}
        searchPlaceholder="Search history…"
        maxHeight="300px"
        renderList={(filteredJobs, loading) => (
          <JobsList
            jobs={filteredJobs}
            loading={loading}
            onDeleteJob={handleDeleteJob}
            hideOutputButton
            hideJobId
            showInteractiveType
            onViewFileBrowser={(jobId) => {
              if (jobId == null || jobId === '') return;
              setViewFileBrowserFromJob(String(jobId));
            }}
          />
        )}
      />
      <DeleteTaskConfirmModal
        open={taskToDelete !== null}
        onClose={() => setTaskToDelete(null)}
        taskIds={taskToDelete ? [taskToDelete.id] : []}
        taskName={taskToDelete?.name ?? null}
        onConfirm={handleConfirmDeleteTask}
      />
      <FileBrowserModal
        mode="job"
        open={viewFileBrowserFromJob !== null}
        onClose={() => setViewFileBrowserFromJob(null)}
        jobId={viewFileBrowserFromJob ?? ''}
      />
    </Sheet>
  );
}
